import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Info, AlertTriangle, MessageSquare, CheckCircle, ArrowUpCircle } from 'lucide-react';
import {
  SectionHeading, StatusBadge, DomainCard, EmptyState, NNSelect,
  FilterBar, FilterSearch, FilterChip, FilterClear,
  Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Tooltip, TooltipTrigger, TooltipContent, ICON_PX, useToast, Input,
} from '../ui';
import { useConsumerUnit } from '../../contexts/ConsumerUnitContext';
import { useAuth } from '../../contexts/AuthContext';
import { addToPriorityQueue, updateComplaintStatus, addComplaintNote } from '../../utils/api';

const STATUS_OPTIONS = ['all', 'open', 'in_progress', 'resolved', 'escalated'];
const CATEGORY_OPTIONS = ['all', 'patient_complaint', 'formal_grievance', 'legal_escalation'];
const URGENCY_OPTIONS = ['all', '5', '4', '3', '2', '1'];

function ConsumerComplaintsTab({ complaints, setComplaints, readOnly }) {
  const { selectedUnitId } = useConsumerUnit();
  const { getToken } = useAuth();
  const { toast } = useToast();
  const queuedRef = useRef(false);

  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [noteDialog, setNoteDialog] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [assignDialog, setAssignDialog] = useState(null);
  const [assignName, setAssignName] = useState('');

  // Priority Queue: legal escalations
  useEffect(() => {
    if (readOnly || queuedRef.current) return;
    const legal = complaints.filter((c) => c.isLegal && c.status === 'open');
    if (legal.length === 0) return;
    queuedRef.current = true;
    legal.forEach((c) => {
      addToPriorityQueue('all', {
        title: `Legal Escalation: ${c.subject}`,
        priority: 'critical', category: 'Consumer', type: 'task',
        estimatedTime: 60, dueDate: new Date().toLocaleDateString('en-CA'),
      }, getToken).catch(() => {});
    });
  }, [complaints, readOnly, getToken]);

  const filtered = useMemo(() => {
    let list = complaints;
    if (selectedUnitId !== 'all') list = list.filter((c) => c.unitId === selectedUnitId);
    if (statusFilter !== 'all') list = list.filter((c) => c.status === statusFilter);
    if (categoryFilter !== 'all') list = list.filter((c) => c.category === categoryFilter);
    if (urgencyFilter !== 'all') list = list.filter((c) => c.urgency === Number(urgencyFilter));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((c) => c.subject.toLowerCase().includes(q) || c.owner?.toLowerCase().includes(q));
    }
    return list;
  }, [complaints, selectedUnitId, statusFilter, categoryFilter, urgencyFilter, search]);

  const activeFilters = [
    statusFilter !== 'all' && `Status: ${statusFilter}`,
    categoryFilter !== 'all' && `Category: ${categoryFilter}`,
    urgencyFilter !== 'all' && `Urgency: ${urgencyFilter}`,
  ].filter(Boolean);

  const updateComplaint = useCallback((id, updates) => {
    setComplaints((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c)));
  }, [setComplaints]);

  const handleAddNote = () => {
    if (!noteDialog || !noteText.trim()) return;
    const id = noteDialog.id;
    const text = noteText.trim();
    addComplaintNote(id, text, getToken)
      .then(() => {
        updateComplaint(id, { notes: [...(noteDialog?.notes ?? []), text] });
        toast({ title: 'Note added' });
      })
      .catch(() => toast({ title: 'Failed to add note', variant: 'destructive' }));
    setNoteDialog(null);
    setNoteText('');
  };

  const handleAssign = () => {
    if (!assignDialog || !assignName.trim()) return;
    updateComplaint(assignDialog.id, { owner: assignName.trim(), status: assignDialog.status === 'open' ? 'in_progress' : assignDialog.status });
    toast({ title: `Assigned to ${assignName.trim()}` });
    setAssignDialog(null);
    setAssignName('');
  };

  return (
    <div className="flex flex-col gap-3">
      <SectionHeading
        size="sm" className="mb-1.5" title="Complaint Tracker"
        action={
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="flex items-center text-text-muted hover:text-text-dark-secondary transition-colors"><Info size={ICON_PX.md} /></button>
            </TooltipTrigger>
            <TooltipContent side="bottom" maxWidth={220}>Patient complaints with resolution workflow. Legal escalations (urgency 5) auto-add to Priority Queue. Complaints &gt;7d auto-escalate to Director.</TooltipContent>
          </Tooltip>
        }
      />

      <FilterBar>
        <FilterSearch value={search} onChange={setSearch} placeholder="Search..." />
        <div className="flex items-center gap-2">
          <NNSelect value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS.map((s) => ({ value: s, label: s === 'all' ? 'All Status' : s.replace('_', ' ') }))} />
          <NNSelect value={categoryFilter} onChange={setCategoryFilter} options={CATEGORY_OPTIONS.map((c) => ({ value: c, label: c === 'all' ? 'All Categories' : c.replace(/_/g, ' ') }))} />
          <NNSelect value={urgencyFilter} onChange={setUrgencyFilter} options={URGENCY_OPTIONS.map((u) => ({ value: u, label: u === 'all' ? 'All Urgency' : `Urgency ${u}` }))} />
        </div>
      </FilterBar>

      {activeFilters.length > 0 && (
        <div className="flex items-center gap-2">
          {activeFilters.map((f) => (
            <FilterChip key={f} label={f} onDismiss={() => {
              if (f.startsWith('Status')) setStatusFilter('all');
              if (f.startsWith('Category')) setCategoryFilter('all');
              if (f.startsWith('Urgency')) setUrgencyFilter('all');
            }} />
          ))}
          <FilterClear count={activeFilters.length} onClick={() => { setStatusFilter('all'); setCategoryFilter('all'); setUrgencyFilter('all'); }} />
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState title="No complaints" body="No complaints match the current filters." />
      ) : (
        <div className="flex flex-col gap-2">
          {filtered.map((c) => (
            <DomainCard
              key={c.id} domain="consumer" accentPlacement="left"
              alertLevel={c.isLegal ? 'critical' : c.daysOpen > 7 ? 'warning' : undefined}
              className="transition-shadow hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                    <StatusBadge variant={c.urgency >= 5 ? 'critical' : c.urgency >= 4 ? 'below-target' : c.urgency >= 3 ? 'watch' : 'info'}>
                      U{c.urgency}
                    </StatusBadge>
                    {c.isLegal && <StatusBadge variant="critical">Legal</StatusBadge>}
                    <StatusBadge variant={c.status === 'resolved' ? 'on-track' : c.status === 'escalated' ? 'critical' : 'info'}>
                      {c.status.replace('_', ' ')}
                    </StatusBadge>
                    {c.daysOpen > 7 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span><StatusBadge variant="watch"><AlertTriangle size={ICON_PX.xs} className="mr-0.5" /> {c.daysOpen}d</StatusBadge></span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" maxWidth={220}>Auto-escalated to Director visibility (&gt;7 days open)</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <p className="text-body font-medium text-text-dark truncate">{c.subject}</p>
                  <div className="flex gap-3 mt-0.5 text-caption text-text-muted">
                    <span>{c.source}</span>
                    <span>{c.category.replace(/_/g, ' ')}</span>
                    <span>{c.daysOpen}d open</span>
                    <span>{c.owner || 'Unassigned'}</span>
                    {(c?.estimatedCost ?? 0) > 0 && <span>${(c?.estimatedCost ?? 0).toLocaleString()}</span>}
                    {selectedUnitId === 'all' && <span>{c.unitName}</span>}
                  </div>
                </div>

                {!readOnly && c.status !== 'resolved' && c.status !== 'escalated' && (
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon-sm" title="Assign" onClick={() => { setAssignDialog(c); setAssignName(c.owner || ''); }}>
                      <MessageSquare size={ICON_PX.md} />
                    </Button>
                    <Button variant="ghost" size="icon-sm" title="Add note" onClick={() => { setNoteDialog(c); setNoteText(''); }}>
                      <MessageSquare size={ICON_PX.md} />
                    </Button>
                    <Button variant="success" size="sm" onClick={() => {
                      updateComplaintStatus(c.id, 'resolved', getToken)
                        .then(() => { updateComplaint(c.id, { status: 'resolved' }); toast({ title: 'Complaint resolved' }); })
                        .catch(() => toast({ title: 'Failed to resolve', variant: 'destructive' }));
                    }}>
                      <CheckCircle size={ICON_PX.sm} className="mr-1" /> Resolve
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => {
                      updateComplaintStatus(c.id, 'escalated', getToken)
                        .then(() => { updateComplaint(c.id, { status: 'escalated', urgency: Math.max(c.urgency, 5) }); toast({ title: 'Complaint escalated' }); })
                        .catch(() => toast({ title: 'Failed to escalate', variant: 'destructive' }));
                    }}>
                      <ArrowUpCircle size={ICON_PX.sm} className="mr-1" /> Escalate
                    </Button>
                  </div>
                )}
              </div>

              {(c?.notes ?? []).length > 0 && (
                <div className="mt-1.5 pl-2 border-l-2 border-nn-border">
                  {(c?.notes ?? []).map((n, i) => (
                    <p key={i} className="text-caption text-text-muted">{n}</p>
                  ))}
                </div>
              )}
            </DomainCard>
          ))}
        </div>
      )}

      {/* Add Note Dialog */}
      <Dialog open={!!noteDialog} onOpenChange={(open) => !open && setNoteDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Note</DialogTitle></DialogHeader>
          <p className="text-body-sm text-text-muted mb-2">{noteDialog?.subject}</p>
          <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Enter note..." />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNoteDialog(null)}>Cancel</Button>
            <Button variant="default" onClick={handleAddNote}>Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Owner Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={(open) => !open && setAssignDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign Owner</DialogTitle></DialogHeader>
          <p className="text-body-sm text-text-muted mb-2">{assignDialog?.subject}</p>
          <Input value={assignName} onChange={(e) => setAssignName(e.target.value)} placeholder="Owner name..." />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignDialog(null)}>Cancel</Button>
            <Button variant="default" onClick={handleAssign}>Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ConsumerComplaintsTab;
